import React, { useState, useRef, useEffect } from "react";
import { Link } from "gatsby";
import {
  useSetCurrentTrack,
  useStoreContext,
  useToggleMediaPlayer,
  useSetCourseMedia,
} from "src/context/siteContext";
import SanityMuxPlayer from "sanity-mux-player";
import MediaControls from "./MediaControls";

import cx from "classnames";
import useInterval from "../../hooks/useInterval";
import useHover from "../../hooks/useHover";
import cookie from "js-cookie";
import { isFinite } from 'lodash-es';

const VideoPlayer = () => {
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMouseMoving, setIsMouseMoving] = useState(false);

  const videoRef = useRef(null);
  const [hoverRef, isHovered] = useHover();
  const [headerHoverRef, isHeaderHovered] = useHover();
  const {
    courseMediaType,
    currentTrack,
    courseMediaFiles = [],
  } = useStoreContext();

  const setMediaPlayerOpen = useToggleMediaPlayer();
  const setCourseMedia = useSetCourseMedia();
  const setCurrentTrack = useSetCurrentTrack();

  const canClickPrev = courseMediaFiles.indexOf(currentTrack) !== 0;
  const canClickNext =
    courseMediaFiles.indexOf(currentTrack) !== courseMediaFiles.length - 1;

  const isTrailer = currentTrack?.title.toLowerCase().includes("trailer");

  useInterval(
    () => {
      const currentProgress = (
        (videoRef.current.video.current.currentTime /
          videoRef.current.video.current.duration) *
        100
      ).toFixed(2);
      setPosition(videoRef.current.video.current.currentTime);
      setProgress(Number(currentProgress));
    },
    isPlaying ? 100 : null
  );

  useInterval(
    () => {
      const currentProgress = (
        (videoRef.current.video.current.currentTime /
          videoRef.current.video.current.duration) *
        100
      ).toFixed(0);
      const currentTime = videoRef.current.video.current.currentTime;

      if (!isTrailer) {
        cookie.set(
          `district-vision__course-track-${currentTrack.id}`,
          {
            currentProgress: parseInt(currentProgress),
            currentSeek: currentTime,
          },
          { expires: 365 }
        );
      }
    },
    isPlaying ? 100 : null
  );

  useInterval(
    () => {
      if (
        videoRef.current &&
        !videoRef.current.state.isLoading &&
        videoRef.current?.video?.current &&
        isFinite(videoRef.current?.video?.current?.duration)
      ) {
        setIsVideoLoaded(true);
      } else {
        setIsVideoLoaded(false);
      }
    },
    !isVideoLoaded && currentTrack ? 100 : null
  );

  const togglePlay = () => {
    const video = videoRef.current.video.current;

    if (isPlaying) {
      video.pause();
      setIsPlaying(false);
    } else {
      video.play();
      setIsPlaying(true);
    }
  };

  const goNext = () => {
    if (!canClickNext) return;
    const nextTrack = courseMediaFiles.indexOf(currentTrack) + 1;
    setCurrentTrack(courseMediaFiles[nextTrack]);
    setIsPlaying(false);
    setIsVideoLoaded(false);
  };

  const goPrev = () => {
    if (!canClickPrev) return;
    const prevTrack = courseMediaFiles.indexOf(currentTrack) - 1;
    setCurrentTrack(courseMediaFiles[prevTrack]);
    setIsPlaying(false);
    setIsVideoLoaded(false);
  };

  const onSeek = (value) => {
    setIsPlaying(false);

    const percentage = value / 100;
    const videoDuration = videoRef.current.video.current.duration;
    const newVideoValue = Number(videoDuration * percentage);

    videoRef.current.video.current.currentTime = newVideoValue;

    setTimeout(() => {
      setIsPlaying(true);
    }, 500);
  };

  const setMouseMove = (e) => {
    e.preventDefault();
    setIsMouseMoving(true);

    setTimeout(() => {
      setIsMouseMoving(false);
    }, 3000);
  };

  useEffect(() => {
    if (courseMediaType === "video" && isVideoLoaded) {
      setTimeout(() => {
        const trackDuration = videoRef.current.video.current.duration;
        setDuration(trackDuration);
        setIsPlaying(true);
        videoRef.current.video.current.play();
      }, 1000);
    }
  }, [courseMediaType, videoRef, currentTrack, isVideoLoaded]);

  useEffect(() => {
    if (
      videoRef.current &&
      videoRef.current.video.current &&
      courseMediaType === "video"
    ) {
      videoRef.current.video.current.addEventListener("ended", goNext);
    }
    return () => {
      if (videoRef.current && videoRef.current.video.current) {
        videoRef.current.video.current.removeEventListener("ended", goNext);
      }
    };
  }, [courseMediaType, videoRef, currentTrack]);

  useEffect(() => {
    const savedProgressCookie =
      currentTrack &&
      !isTrailer &&
      cookie.get(`district-vision__course-track-${currentTrack.id}`);
    const parsedProgress =
      savedProgressCookie && JSON.parse(savedProgressCookie);

    if (currentTrack && parsedProgress && isVideoLoaded && !isTrailer) {
      setTimeout(() => {
        setIsPlaying(false);
        if (parsedProgress.currentProgress < 99) {
          const percentage = Number(parsedProgress.currentProgress) / 100;
          const videoDuration = videoRef.current.video.current.duration;
          const newVideoValue = Number(videoDuration * percentage);

          if (isFinite(newVideoValue)) {
            videoRef.current.video.current.currentTime = newVideoValue;
          }
        } else {
          videoRef.current.video.current.currentTime = 0;
        }
        setIsPlaying(true);
      }, 1000);
    }
  }, [courseMediaType, videoRef, isVideoLoaded, currentTrack]);

  useEffect(() => {
    if (isVideoLoaded && videoRef.current) {
      if (isPlaying) {
        videoRef.current.video.current.play();
      } else {
        videoRef.current.video.current.pause();
      }
    }
  }, [isVideoLoaded, isPlaying, videoRef, videoRef.current]);

  useEffect(() => {
    // clean up
    if (courseMediaType === "video" && currentTrack) {
      document.body.classList.add("bg-black");
    }

    return () => {
      setIsPlaying(false);
      setIsVideoLoaded(false);
      if (!currentTrack || !courseMediaType) {
        setMediaPlayerOpen(false);
        setIsPlaying(false);
        setProgress(0);
        setCourseMedia(undefined, []);
        setCurrentTrack(undefined);
        document.body.classList.remove("bg-black");
      }
    };
  }, [courseMediaType, currentTrack]);

  return (
    <div onMouseMove={setMouseMove} className="media-player--video">
      {currentTrack && (
        <React.Fragment>
          <div
            ref={headerHoverRef}
            className={cx("course-video__header fix top left right", {
              hovered: isHeaderHovered || isMouseMoving,
            })}
          >
            <Link
              to="/account"
              className="course-video__header-logo"
              onClick={() => {
                setMediaPlayerOpen(false);
                setIsPlaying(false);
                setProgress(0);
                setCourseMedia(undefined, []);
                setCurrentTrack(undefined);
              }}
            >
              <img src="/images/logo_white.svg" alt="DV Logo" />
            </Link>
            <button
              className="c-white course-video__header-close"
              onClick={() => {
                setMediaPlayerOpen(false);
                setIsPlaying(false);
                setProgress(0);
                setCourseMedia(undefined, []);
                setCurrentTrack(undefined);
              }}
            >
              X <span className="underline-on-hover">Close</span>
            </button>
          </div>

          <SanityMuxPlayer
            ref={videoRef}
            assetDocument={currentTrack.video}
            autoload
            showControls={false}
            playsInline
            className="course-video"
          />

          <div
            ref={hoverRef}
            className="course-video__hover-trigger fix bottom left right"
          >
            <MediaControls
              classNames={cx("media-controls--video fix bottom left right", {
                hovered: isHovered || isMouseMoving,
              })}
              title={`${courseMediaFiles.indexOf(currentTrack) + 1}. ${
                currentTrack.title
              }`}
              progress={progress}
              position={position}
              duration={duration}
              isPlaying={isPlaying}
              onSeek={onSeek}
              canClickPrev={canClickPrev}
              canClickNext={canClickNext}
              onClickNext={goNext}
              onClickPrev={goPrev}
              onTogglePlayback={togglePlay}
              isVideoLoaded={isVideoLoaded}
            />
          </div>
          {!isVideoLoaded && (
            <div className="course-video__loading fix top left right bottom">
              <p>Loading...</p>
            </div>
          )}
        </React.Fragment>
      )}
    </div>
  );
};;

export default VideoPlayer;
